import React, { useRef, cloneElement, isValidElement, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { oneOf, bool, element, string, func } from 'prop-types';
import useModal from '../../hooks/useModal';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { Footer, Body, Header } from './ModalContent';

import './modal.scss';

const SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * UI component for modal.
 */
const Modal = (
  {
    size,
    component,
    className = '',
    closeOnClickOutside = false,
    open: openAsProp,
    onConfirm: onConfirmAsProp,
    onClose: onCloseAsProp,
    errorBoundaryComponent: ErrorBoundaryComponent,
  },
  ref
) => {
  if (!component) throw new Error('component prop is required');

  const modalWrapper = useRef(null);
  const modalSize = SIZES.find((s) => s === size) || 'md';
  const { open, close, isOpen, onConfirm, onClose, modalProps } = useModal(
    onConfirmAsProp,
    onCloseAsProp
  );
  const clickOutsideHandler = () => closeOnClickOutside && onClose();
  const isModalOpen = isOpen || openAsProp;

  useOnClickOutside(modalWrapper, clickOutsideHandler);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  if (!isModalOpen) return null;

  const componentWithProps = React.Children.map(component, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { ...modalProps, onConfirm, onClose });
    }
    return child;
  });

  return ReactDOM.createPortal(
    <div className={`modal-wrapper ${className}`}>
      <div aria-hidden="true" className="modal-backdrop" />
      <div className="modal-root" role="presentation" tabIndex="-1">
        <div ref={modalWrapper} className={`modal ${modalSize}`}>
          {ErrorBoundaryComponent ? (
            <ErrorBoundaryComponent onClose={onClose}>{componentWithProps}</ErrorBoundaryComponent>
          ) : (
            componentWithProps
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ModalForwardRef = React.forwardRef(Modal);

ModalForwardRef.Header = Header;
ModalForwardRef.Body = Body;
ModalForwardRef.Footer = Footer;

ModalForwardRef.propTypes = {
  size: oneOf(SIZES),
  component: element.isRequired,
  className: string,
  closeOnClickOutside: bool,
  onConfirm: func,
  onClose: func,
};

ModalForwardRef.defaultProps = {
  size: 'md',
  closeOnClickOutside: false,
};

export default ModalForwardRef;
